import React, { useState } from 'react';
import { contactData } from '../data/mock';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Frontend mock validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Mock success response
    toast.success('Message envoyé avec succès ! Nous vous répondrons rapidement.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-gradient-to-b from-white to-rose-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl lg:text-5xl font-light text-gray-800 mb-4 tracking-tight">
            Contactez-nous
          </h2>
          <p className="text-lg text-rose-400 uppercase tracking-widest font-medium">
            Nous sommes à votre écoute
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="font-serif text-2xl text-gray-800 mb-6">
                Informations pratiques
              </h3>
              
              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                    <MapPin className="text-rose-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800 mb-1">Adresse</h4>
                    <p className="text-gray-600">
                      {contactData.address.name}<br />
                      {contactData.address.street}<br />
                      {contactData.address.city}<br />
                      {contactData.address.country}
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                    <Phone className="text-rose-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800 mb-1">Téléphone</h4>
                    <a 
                      href={`tel:${contactData.address.phone}`}
                      className="text-gray-600 hover:text-rose-400 transition-colors duration-300"
                    >
                      {contactData.address.phone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                    <Mail className="text-rose-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800 mb-1">Email</h4>
                    <a 
                      href={`mailto:${contactData.address.email}`}
                      className="text-gray-600 hover:text-rose-400 transition-colors duration-300"
                    >
                      {contactData.address.email}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                    <Clock className="text-rose-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800 mb-1">Horaires</h4>
                    <p className="text-gray-600">
                      {contactData.hours.weekdays}<br />
                      {contactData.hours.sunday}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-gray-200 rounded-lg h-64 flex items-center justify-center">
              <p className="text-gray-500 text-sm">
                Près de la Place de la Concorde, Paris
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
            <h3 className="font-serif text-2xl text-gray-800 mb-6">
              Envoyez-nous un message
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-gray-700 mb-2 block">
                  Nom complet *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Votre nom"
                  className="w-full border-gray-300 focus:border-rose-400 focus:ring-rose-400"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-gray-700 mb-2 block">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre.email@exemple.com"
                  className="w-full border-gray-300 focus:border-rose-400 focus:ring-rose-400"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-gray-700 mb-2 block">
                  Téléphone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+33 6 12 34 56 78"
                  className="w-full border-gray-300 focus:border-rose-400 focus:ring-rose-400"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-gray-700 mb-2 block">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Parlez-nous de votre recherche..."
                  rows={5}
                  className="w-full border-gray-300 focus:border-rose-400 focus:ring-rose-400 resize-none"
                  required
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-rose-400 hover:bg-rose-500 text-white py-6 text-base uppercase tracking-wider font-medium rounded-sm transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Envoyer le message
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
